import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestUtils } from '../../shared/services/rest-utils';
import {
  AutocloseTicketCollection,
  AutoModeState,
  CaseChangeHistoryEntry,
  CaseWithHistory,
  ClosedManualTicketCollection,
  ManualTicketCollection,
  PromptConfig,
  SupportCase,
  TicketActionResult,
  TicketDetail,
  TicketReplyCount,
  TicketReviewCollection,
} from './support.model';

const BASE = 'ROLE/support-workflows/api/support';

@Injectable()
export class SupportService {
  utils = new RestUtils();
  headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(private http: HttpClient) {}

  // ─── Manual Tickets ──────────────────────────────────────────────

  getManualTickets(): Observable<ManualTicketCollection> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<ManualTicketCollection>(url, options);
  }

  getManualTicketDetail(ticketId: number): Observable<TicketDetail> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<TicketDetail>(url, options);
  }

  solveTicket(ticketId: number): Observable<TicketActionResult> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}/solve`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<TicketActionResult>(url, {}, options);
  }

  closeTicket(ticketId: number): Observable<TicketActionResult> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}/close`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<TicketActionResult>(url, {}, options);
  }

  spamTicket(ticketId: number): Observable<TicketActionResult> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}/spam`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<TicketActionResult>(url, {}, options);
  }

  retryTicket(ticketId: number): Observable<TicketActionResult> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}/retry`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<TicketActionResult>(url, {}, options);
  }

  updateProposedReply(ticketId: number, message: string): Observable<TicketActionResult> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}/reply`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<TicketActionResult>(url, { message }, options);
  }

  reviseTicketReview(ticketId: number, comment: string): Observable<TicketActionResult> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/${ticketId}/revise`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<TicketActionResult>(url, { comment }, options);
  }

  // ─── Closed Manual Tickets ─────────────────────────────────────

  getClosedManualTickets(params: { days?: number; limit?: number; offset?: number } = {}): Observable<ClosedManualTicketCollection> {
    const url = this.utils.buildUrl(`${BASE}/manual-tickets/history`, params);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<ClosedManualTicketCollection>(url, options);
  }

  // ─── Auto Mode ──────────────────────────────────────────────────

  getAutoModeState(): Observable<AutoModeState> {
    const url = this.utils.buildUrl(`${BASE}/auto-mode`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<AutoModeState>(url, options);
  }

  toggleAutoMode(enabled: boolean): Observable<{ ok: boolean; enabled: boolean }> {
    const url = this.utils.buildUrl(`${BASE}/auto-mode`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<{ ok: boolean; enabled: boolean }>(url, { enabled }, options);
  }

  resetAutoMode(): Observable<{ ok: boolean; clearedManualTickets: number; clearedRecentTickets: number }> {
    const url = this.utils.buildUrl(`${BASE}/auto-mode/reset`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<{ ok: boolean; clearedManualTickets: number; clearedRecentTickets: number }>(url, {}, options);
  }

  // ─── Autoclose Tickets ──────────────────────────────────────────

  getAutocloseTickets(params: { days?: number; limit?: number; offset?: number } = {}): Observable<AutocloseTicketCollection> {
    const url = this.utils.buildUrl(`${BASE}/autoclose-tickets`, params);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<AutocloseTicketCollection>(url, options);
  }

  getAutocloseTicketDetail(ticketId: number): Observable<TicketDetail> {
    const url = this.utils.buildUrl(`${BASE}/autoclose-tickets/${ticketId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<TicketDetail>(url, options);
  }

  // ─── Ticket Detail ──────────────────────────────────────────────

  getTicketDetail(ticketId: number): Observable<TicketDetail> {
    const url = this.utils.buildUrl(`${BASE}/tickets/${ticketId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<TicketDetail>(url, options);
  }

  getTicketThreadDetail(ticketId: number): Observable<TicketDetail> {
    const url = this.utils.buildUrl(`${BASE}/tickets/${ticketId}/thread`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<TicketDetail>(url, options);
  }

  getTicketReplyCount(ticketId: number): Observable<TicketReplyCount> {
    const url = this.utils.buildUrl(`${BASE}/tickets/${ticketId}/reply-count`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<TicketReplyCount>(url, options);
  }

  getTicketReviews(params: { limit?: number; offset?: number } = {}): Observable<TicketReviewCollection> {
    const url = this.utils.buildUrl(`${BASE}/ticket-reviews`, params);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<TicketReviewCollection>(url, options);
  }

  // ─── Support Cases ──────────────────────────────────────────────

  getCases(): Observable<{ items: SupportCase[] }> {
    const url = this.utils.buildUrl(`${BASE}/cases`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<{ items: SupportCase[] }>(url, options);
  }

  getCaseDetail(id: number): Observable<CaseWithHistory> {
    const url = this.utils.buildUrl(`${BASE}/cases/${id}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<CaseWithHistory>(url, options);
  }

  createCase(data: { name: string; instructions: string; autoclose?: boolean; sortOrder?: number }): Observable<SupportCase> {
    const url = this.utils.buildUrl(`${BASE}/cases`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<SupportCase>(url, data, options);
  }

  updateCase(id: number, data: { name?: string; instructions?: string; autoclose?: boolean; sortOrder?: number }): Observable<SupportCase> {
    const url = this.utils.buildUrl(`${BASE}/cases/${id}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<SupportCase>(url, data, options);
  }

  deleteCase(id: number): Observable<{ ok: boolean }> {
    const url = this.utils.buildUrl(`${BASE}/cases/${id}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.delete<{ ok: boolean }>(url, options);
  }

  toggleAutoclose(id: number, autoclose: boolean): Observable<SupportCase> {
    const url = this.utils.buildUrl(`${BASE}/cases/${id}/autoclose`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.patch<SupportCase>(url, { autoclose }, options);
  }

  getCaseHistory(id: number): Observable<{ items: CaseChangeHistoryEntry[] }> {
    const url = this.utils.buildUrl(`${BASE}/cases/${id}/history`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<{ items: CaseChangeHistoryEntry[] }>(url, options);
  }

  // ─── Prompt Config ───────────────────────────────────────────────

  getPromptConfig(): Observable<{ items: PromptConfig[] }> {
    const url = this.utils.buildUrl(`${BASE}/prompt-config`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<{ items: PromptConfig[] }>(url, options);
  }

  updatePromptConfig(key: string, content: string): Observable<PromptConfig> {
    const url = this.utils.buildUrl(`${BASE}/prompt-config/${key}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<PromptConfig>(url, { content }, options);
  }

  getPromptConfigHistory(key: string): Observable<{ items: CaseChangeHistoryEntry[] }> {
    const url = this.utils.buildUrl(`${BASE}/prompt-config/${key}/history`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<{ items: CaseChangeHistoryEntry[] }>(url, options);
  }
}
